import { AxiosResponse } from "axios";

import { coreService } from "../../../services";
import {
  ApiGifOverlayResponse,
  ApiTextOverlayResponse,
  BackgroundStyle,
  ExportVideoCompressionLevel,
  ExportVideoResolution,
  GifOverlay,
  Paragraph,
  SubtitleStyles,
  TextOverlay,
  VideoResponse,
} from "../types";

interface ExportShortVideoPayload {
  video_resolution: ExportVideoResolution;
  compression_level: ExportVideoCompressionLevel;
  fps: number;
  include_subtitle: boolean;
  font?: string;
}

const ALLOWED_FONTS = [
  "Inter",
  "Montserrat",
  "Outfit",
  "Bebas Neue",
  "Oswald",
  "Josefin Sans",
  "Playfair Display",
  "Arvo",
] as const;

// --- Response mappers (API → FE types) ---

export const mapApiTextOverlayToFe = (
  o: ApiTextOverlayResponse,
  videoDurationMs: number,
): TextOverlay => ({
  id: o.id,
  text: o.text,
  fontFamily: ALLOWED_FONTS.includes(o.font as (typeof ALLOWED_FONTS)[number])
    ? (o.font as string)
    : "Inter",
  fontSize: o.size ?? 24,
  color: o.color ?? "#ffffff",
  backgroundColor: o.background_color ?? "rgba(0,0,0,0.5)",
  backgroundStyle: o.background_style ?? BackgroundStyle.SOLID,
  bold: o.bold ?? false,
  italic: o.italic ?? false,
  underline: o.underline ?? false,
  strikethrough: o.strikethrough ?? false,
  letterCase: o.letter_case,
  letterSpacing: o.letter_spacing,
  textAlign: o.text_align,
  borderColor: o.border_color,
  borderWidth: o.border_width,
  borderRadius: o.border_radius,
  borderStyle: o.border_style,
  x: o.position_x_percentage ?? 30,
  y: o.position_y_percentage ?? 40,
  width: o.width_percentage ?? 40,
  height: 15,
  startMs: o.start != null ? o.start * 1000 : 0,
  endMs: o.end != null ? o.end * 1000 : videoDurationMs,
  animation: (o.animation as TextOverlay["animation"]) ?? "none",
});

export const mapApiGifOverlayToFe = (
  o: ApiGifOverlayResponse,
  videoDurationMs: number,
): GifOverlay => ({
  id: o.id,
  giphyId: o.giphy_url.match(/\/media\/([^/]+)\//)?.[1] ?? "",
  title: "GIF",
  url: o.giphy_url,
  originalUrl: o.giphy_url,
  x: o.position_x_percentage,
  y: o.position_y_percentage,
  width: o.width_percentage,
  height: 30,
  startMs: o.start != null ? o.start * 1000 : 0,
  endMs: o.end != null ? o.end * 1000 : videoDurationMs,
});

interface ApiTextOverlay {
  id: string;
  text: string;
  font?: string;
  size?: number;
  color?: string;
  background_color?: string;
  background_style?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  letter_case?: string;
  letter_spacing?: number;
  text_align?: string;
  border_color?: string;
  border_width?: number;
  border_radius?: number;
  border_style?: string;
  animation?: string;
  position_x_percentage?: number;
  position_y_percentage?: number;
  width_percentage?: number;
  start?: number;
  end?: number;
}

interface ApiGifOverlay {
  id: string;
  giphy_url: string;
  position_x_percentage: number;
  position_y_percentage: number;
  width_percentage: number;
  start?: number;
  end?: number;
}

const mapTextOverlayToApi = (
  overlay: TextOverlay,
  videoDurationMs: number,
): ApiTextOverlay => {
  const isFullDuration =
    overlay.startMs === 0 && overlay.endMs >= videoDurationMs;
  return {
    id: overlay.id,
    text: overlay.text,
    font: ALLOWED_FONTS.includes(
      overlay.fontFamily as (typeof ALLOWED_FONTS)[number],
    )
      ? overlay.fontFamily
      : undefined,
    size: overlay.fontSize,
    color: overlay.color,
    background_color: overlay.backgroundColor,
    background_style: overlay.backgroundStyle,
    bold: overlay.bold,
    italic: overlay.italic,
    underline: overlay.underline,
    strikethrough: overlay.strikethrough,
    letter_case: overlay.letterCase,
    letter_spacing: overlay.letterSpacing,
    text_align: overlay.textAlign,
    border_color: overlay.borderColor,
    border_width: overlay.borderWidth,
    border_radius: overlay.borderRadius,
    border_style: overlay.borderStyle,
    animation: overlay.animation,
    position_x_percentage: overlay.x,
    position_y_percentage: overlay.y,
    width_percentage: overlay.width,
    ...(isFullDuration
      ? {}
      : {
          start: overlay.startMs / 1000,
          end: overlay.endMs / 1000,
        }),
  };
};

const mapGifOverlayToApi = (
  overlay: GifOverlay,
  videoDurationMs: number,
): ApiGifOverlay => {
  const isFullDuration =
    overlay.startMs === 0 && overlay.endMs >= videoDurationMs;
  return {
    id: overlay.id,
    giphy_url: overlay.originalUrl,
    position_x_percentage: overlay.x,
    position_y_percentage: overlay.y,
    width_percentage: overlay.width,
    ...(isFullDuration
      ? {}
      : {
          start: overlay.startMs / 1000,
          end: overlay.endMs / 1000,
        }),
  };
};

const getVideo = async (
  episodeId: string | undefined,
): Promise<VideoResponse | undefined> => {
  if (episodeId) {
    const response = await coreService.httpClient.get(
      `/short-videos/${episodeId}`,
    );

    return response.data;
  }

  return undefined;
};

const updateShortVideo = async (
  id: string,
  data: {
    subtitleStyles?: SubtitleStyles;
    paragraph?: Paragraph;
    textOverlays?: TextOverlay[];
    gifOverlays?: GifOverlay[];
    videoDurationMs?: number;
    cropPositions?: Record<string, { x: number; y: number }>;
  },
): Promise<AxiosResponse<void>> => {
  const body: Record<string, unknown> = {};

  if (data.subtitleStyles !== undefined) {
    body.subtitle_styles = data.subtitleStyles;
  }
  if (data.paragraph !== undefined) {
    body.paragraph = data.paragraph;
  }
  if (data.textOverlays !== undefined) {
    body.text_overlays = data.textOverlays.map((o) =>
      mapTextOverlayToApi(o, data.videoDurationMs ?? 0),
    );
  }
  if (data.gifOverlays !== undefined) {
    body.gif_overlays = data.gifOverlays.map((o) =>
      mapGifOverlayToApi(o, data.videoDurationMs ?? 0),
    );
  }
  if (data.cropPositions !== undefined) {
    body.crop_positions = data.cropPositions;
  }

  return coreService.httpClient.patch(`/short-videos/${id}`, body);
};

const exportShortVideo = async (
  id: string,
  payload: ExportShortVideoPayload,
): Promise<AxiosResponse<void>> => {
  return coreService.httpClient.post(`/short-videos/${id}/export`, payload);
};

export { getVideo, updateShortVideo, exportShortVideo };
